import { ReactElement, Suspense, useEffect } from 'react';
import { Provider } from 'react-redux';
import { Switch, Route } from 'react-router-dom';
import { Router, matchPath } from 'react-router';
import { PersistGate } from 'redux-persist/integration/react';
import { createBrowserHistory } from 'history';
import * as Sentry from '@sentry/react';

import routes, { AppRoute, RouteParams } from './pages/routes';
import configureStore from './store';

import './App.css';
import { track, TRACK_ROUTE_MISSING } from './tracking/mixpanel';
import { SENTRY_DNS } from './api/constants';

SENTRY_DNS &&
    Sentry.init({
        dsn: SENTRY_DNS,
        tracesSampleRate: 0,
        beforeSend(event) {
            if (
                event.message === 'ResizeObserver loop limit exceeded' ||
                event.message === 'ResizeObserver loop completed with undelivered notifications.'
            ) {
                return null;
            }
            return event;
        },
    });

const { store, persistor } = configureStore();

const his = createBrowserHistory();

interface CurrentRouteWithParams extends AppRoute {
    params: RouteParams;
}

export let cachedRouteParams = {};

export const getCurrentRouteAndParams = (
    url: string,
    search: string,
): CurrentRouteWithParams | void => {
    const searchParams = new URLSearchParams(search);
    let sp: { [k: string]: string | null } = {};

    for (const key of searchParams.keys()) {
        sp = { ...sp, [key]: searchParams.get(key) };
    }

    for (let i = 0; i < routes.length; i++) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const mp = matchPath<RouteParams>(url, routes[i]);
        if (mp) {
            cachedRouteParams = { ...mp.params, sp };
            return { ...routes[i], params: mp.params };
        } else {
            cachedRouteParams = sp;
        }
    }
};

his.listen((location, action) => {
    trackRouteChange();
});

function trackRouteChange() {
    const route = getCurrentRouteAndParams(window.location.pathname, window.location.search);
    if (route && route.pageTrackingName) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        track(route.pageTrackingName, { ...route.params });
    } else {
        track(TRACK_ROUTE_MISSING, { path: window.location.pathname });
    }
}

export const getApp = (): ReactElement => (
    <Router history={his}>
        <Suspense fallback={<p>Fallback loading</p>}>
            <Switch>
                {routes.map(({ path, exact, component: C }) => {
                    return (
                        <Route
                            key={path}
                            path={path}
                            exact={exact}
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            render={(props) => <C {...props} />}
                        />
                    );
                })}
            </Switch>
        </Suspense>
    </Router>
);

function App(): ReactElement {
    useEffect(trackRouteChange, []);
    return (
        <Provider store={store}>
            {/* // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore */}
            <PersistGate persistor={persistor} loading={<span>Loading state</span>}>
                {getApp()}
            </PersistGate>
        </Provider>
    );
}

export default App;

// const ORIGINAL_WINDOW_HEIGHT = window.innerHeight;

// const reportWindowSize = () => {
//     return;
// };

// window.onresize = reportWindowSize;
